import React, { useEffect, useState } from "react";

/**
 * SocialReel — vertical (1080x1920) scripted animation for TikTok / IG Reels.
 * Rendered as a headless-recorded page. NOT for public browsing.
 * Total runtime: 24s (6 scenes × 4s each).
 *
 * Recorded by /app/scripts/record_social_reel.py at portrait viewport.
 */

const SCENES = [
  { id: 0, at: 0,    ms: 4000 },
  { id: 1, at: 4000, ms: 4000 },
  { id: 2, at: 8000, ms: 4000 },
  { id: 3, at: 12000, ms: 4000 },
  { id: 4, at: 16000, ms: 4000 },
  { id: 5, at: 20000, ms: 4000 },
];

const TOTAL_MS = 24000;

export default function SocialReel() {
  const [t, setT] = useState(0);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = (now - start) % TOTAL_MS;
      setT(elapsed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Which scene is currently active
  const scene = SCENES.findIndex((s) => t >= s.at && t < s.at + s.ms);

  return (
    <div
      data-testid="social-reel"
      style={{
        width: 1080,
        height: 1920,
        background: "#F2EDE2",
        color: "#0a0a0a",
        overflow: "hidden",
        position: "relative",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      }}
    >
      {/* Brand marker (persistent, top-left) */}
      <div
        style={{
          position: "absolute",
          top: 56,
          left: 56,
          display: "flex",
          alignItems: "center",
          gap: 14,
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
          <div style={{ width: 10, height: 30, background: "#7C3AED", borderRadius: 2 }} />
          <div style={{ width: 10, height: 22, background: "#EC4899", borderRadius: 2 }} />
          <div style={{ width: 10, height: 16, background: "#F97316", borderRadius: 2 }} />
        </div>
        <div
          style={{
            fontFamily: "Georgia, serif",
            fontSize: 32,
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          Fount Stories
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: 6,
          width: `${(t / TOTAL_MS) * 100}%`,
          background: "linear-gradient(90deg,#7C3AED 0%,#EC4899 50%,#F97316 100%)",
          zIndex: 60,
        }}
      />

      {scene === 0 && <SceneHook />}
      {scene === 1 && <SceneProblem />}
      {scene === 2 && <SceneFeed />}
      {scene === 3 && <SceneBrief />}
      {scene === 4 && <SceneCalendar />}
      {scene === 5 && <SceneCTA />}
    </div>
  );
}

/* -------------------- Scenes -------------------- */

function SceneHook() {
  return (
    <div style={sceneWrap}>
      <div style={{ ...kicker, color: "#7C3AED" }}>For creators & podcasters</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 148,
          lineHeight: 1.02,
          letterSpacing: "-0.03em",
          fontWeight: 700,
          marginTop: 32,
          animation: "fadeUp 800ms ease-out both",
        }}
      >
        The news is <br />
        <span style={gradText}>loud.</span>
      </div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 96,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 400,
          fontStyle: "italic",
          color: "#374151",
          marginTop: 48,
          animation: "fadeUp 800ms 500ms ease-out both",
        }}
      >
        Your voice <br />
        shouldn&apos;t be.
      </div>
      <Keyframes />
    </div>
  );
}

function SceneProblem() {
  const headlines = [
    "AI startup raises $200M Series B",
    "Streaming subscribers hit new low",
    "Podcast ad market up 24% YoY",
    "Creator economy platforms consolidate",
    "TikTok Shop expands to five markets",
    "Election year drives ad spend record",
    "Substack rolls out video for writers",
    "YouTube caps midroll ad frequency",
    "Netflix acquires indie studio",
    "OpenAI ships new agent framework",
    "Meta downranks external links",
    "Spotify tests AI-hosted podcasts",
  ];
  return (
    <div style={sceneWrap}>
      <div style={kicker}>The problem</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 108,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 600,
          marginTop: 24,
        }}
      >
        Every morning, <br />
        <span style={gradText}>a firehose.</span>
      </div>

      {/* Scrolling headline column */}
      <div
        style={{
          marginTop: 60,
          position: "relative",
          height: 900,
          overflow: "hidden",
          maskImage:
            "linear-gradient(to bottom, transparent 0, #000 12%, #000 88%, transparent 100%)",
        }}
      >
        <div
          style={{
            animation: "scrollUp 4000ms linear both",
            display: "flex",
            flexDirection: "column",
            gap: 22,
          }}
        >
          {[...headlines, ...headlines].map((h, i) => (
            <div
              key={`${h}-${i}`}
              style={{
                padding: "24px 28px",
                background: "#ffffff",
                border: "1px solid #e7e3da",
                borderRadius: 18,
                fontSize: 36,
                fontWeight: 500,
                color: "#111",
                boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 16,
                  color: "#9ca3af",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                {(i % 4 === 0 && "The Verge") ||
                  (i % 4 === 1 && "Bloomberg") ||
                  (i % 4 === 2 && "Axios") ||
                  "TechCrunch"}
              </div>
              {h}
            </div>
          ))}
        </div>
      </div>
      <Keyframes />
    </div>
  );
}

function SceneFeed() {
  return (
    <div style={sceneWrap}>
      <div style={{ ...kicker, color: "#7C3AED" }}>Scene 01 · Feed</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 108,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 600,
          marginTop: 24,
        }}
      >
        72 hours of signal, <br />
        <span style={gradText}>curated for you.</span>
      </div>

      {/* Fake feed card */}
      <div
        style={{
          marginTop: 60,
          background: "#ffffff",
          border: "1px solid #e7e3da",
          borderRadius: 24,
          padding: 40,
          boxShadow: "0 30px 80px rgba(0,0,0,0.08)",
          animation: "fadeUp 800ms 200ms ease-out both",
        }}
      >
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 18,
            color: "#7C3AED",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Podcasting · 2h ago
        </div>
        <div
          style={{
            fontFamily: "Georgia, serif",
            fontSize: 56,
            fontWeight: 600,
            lineHeight: 1.15,
            marginTop: 14,
            color: "#0a0a0a",
          }}
        >
          Spotify tests AI-hosted podcasts in three new markets
        </div>
        <div style={{ marginTop: 22, fontSize: 26, color: "#4b5563", lineHeight: 1.5 }}>
          A limited pilot lets creators generate a synthetic co-host trained on their own back
          catalogue — raising fresh questions about voice ownership.
        </div>

        <div
          style={{
            marginTop: 36,
            padding: 26,
            background: "#faf7f1",
            borderLeft: "4px solid #7C3AED",
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 600, color: "#7C3AED", letterSpacing: "0.1em" }}>
            TALKING POINTS
          </div>
          <ul
            style={{
              margin: "16px 0 0 0",
              padding: 0,
              listStyle: "none",
              fontSize: 28,
              lineHeight: 1.5,
              color: "#111",
            }}
          >
            <li style={{ padding: "8px 0" }}>01. What &ldquo;authenticity&rdquo; means when your host is code.</li>
            <li style={{ padding: "8px 0" }}>02. The voice-cloning consent gap for indie creators.</li>
            <li style={{ padding: "8px 0" }}>03. Who owns your listener data when Spotify hosts you?</li>
          </ul>
        </div>
      </div>
      <Keyframes />
    </div>
  );
}

function SceneBrief() {
  return (
    <div style={sceneWrap}>
      <div style={{ ...kicker, color: "#EC4899" }}>Scene 02 · Daily Brief</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 108,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 600,
          marginTop: 24,
        }}
      >
        3 stories. <br />
        Talking points. <br />
        <span style={gradText}>6am. Every day.</span>
      </div>

      {/* Envelope illustration */}
      <div
        style={{
          marginTop: 80,
          background: "#ffffff",
          border: "1px solid #e7e3da",
          borderRadius: 28,
          padding: 44,
          boxShadow: "0 30px 80px rgba(0,0,0,0.08)",
          animation: "fadeUp 800ms 200ms ease-out both",
        }}
      >
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 18,
            color: "#9ca3af",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Inbox · 06:00 UTC
        </div>
        <div
          style={{
            fontFamily: "Georgia, serif",
            fontSize: 60,
            fontWeight: 600,
            marginTop: 20,
            color: "#0a0a0a",
          }}
        >
          Fount Daily Brief
        </div>
        <div
          style={{
            marginTop: 30,
            display: "grid",
            gap: 18,
            fontSize: 30,
            color: "#374151",
            lineHeight: 1.4,
          }}
        >
          <BriefRow n="01" label="Streaming subscribers hit new low" />
          <BriefRow n="02" label="Podcast ad market up 24% YoY" />
          <BriefRow n="03" label="Substack rolls out video for writers" />
        </div>
        <div
          style={{
            marginTop: 34,
            display: "inline-block",
            padding: "16px 28px",
            background: "linear-gradient(90deg,#7C3AED 0%,#EC4899 50%,#F97316 100%)",
            color: "#fff",
            borderRadius: 12,
            fontSize: 24,
            fontWeight: 600,
          }}
        >
          Open Fount →
        </div>
      </div>
      <Keyframes />
    </div>
  );
}

function SceneCalendar() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const topics = [
    "Voice cloning ethics",
    "Ad market flip",
    "Creator burnout",
    "TikTok Shop pivot",
    "AI-hosted pods",
    "Weekend deep dive",
    "Signal reset",
  ];
  return (
    <div style={sceneWrap}>
      <div style={{ ...kicker, color: "#F97316" }}>Scene 03 · Calendar</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 108,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          fontWeight: 600,
          marginTop: 24,
        }}
      >
        A week of topics, <br />
        <span style={gradText}>ready before Monday.</span>
      </div>

      <div
        style={{
          marginTop: 70,
          display: "grid",
          gridTemplateColumns: "repeat(1, 1fr)",
          gap: 16,
          animation: "fadeUp 800ms 200ms ease-out both",
        }}
      >
        {days.map((d, i) => (
          <div
            key={d}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
              background: "#ffffff",
              border: "1px solid #e7e3da",
              borderRadius: 18,
              padding: "22px 28px",
            }}
          >
            <div
              style={{
                width: 96,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 20,
                color: "#7C3AED",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              {d}
            </div>
            <div style={{ fontSize: 34, color: "#0a0a0a", fontWeight: 500 }}>{topics[i]}</div>
          </div>
        ))}
      </div>
      <Keyframes />
    </div>
  );
}

function SceneCTA() {
  return (
    <div style={{ ...sceneWrap, justifyContent: "center", display: "flex", flexDirection: "column" }}>
      <div style={{ ...kicker, color: "#7C3AED", textAlign: "center" }}>Start free · 10 briefs on us</div>
      <div
        style={{
          fontFamily: "Georgia, serif",
          fontSize: 156,
          lineHeight: 1.02,
          letterSpacing: "-0.03em",
          fontWeight: 700,
          textAlign: "center",
          marginTop: 48,
        }}
      >
        Sound like <br />
        <span style={gradText}>only you.</span>
      </div>

      <div
        style={{
          marginTop: 100,
          textAlign: "center",
          animation: "pulse 1500ms 300ms ease-in-out infinite",
        }}
      >
        <div
          style={{
            display: "inline-block",
            padding: "34px 64px",
            background: "linear-gradient(90deg,#7C3AED 0%,#EC4899 50%,#F97316 100%)",
            color: "#fff",
            borderRadius: 20,
            fontSize: 44,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            boxShadow: "0 30px 80px rgba(124,58,237,0.35)",
          }}
        >
          fountstories.com
        </div>
      </div>

      <div
        style={{
          marginTop: 80,
          textAlign: "center",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 22,
          color: "#6b7280",
          letterSpacing: "0.24em",
          textTransform: "uppercase",
        }}
      >
        Signal · Not noise
      </div>
      <Keyframes />
    </div>
  );
}

/* -------------------- Bits -------------------- */

function BriefRow({ n, label }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 18 }}>
      <div
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          color: "#7C3AED",
          fontWeight: 700,
          fontSize: 30,
          minWidth: 60,
        }}
      >
        {n}
      </div>
      <div style={{ fontSize: 32, color: "#111", lineHeight: 1.35 }}>{label}</div>
    </div>
  );
}

const sceneWrap = {
  position: "absolute",
  inset: 0,
  padding: "220px 80px 80px 80px",
  animation: "sceneIn 600ms ease-out both",
};

const kicker = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 22,
  color: "#7C3AED",
  letterSpacing: "0.28em",
  textTransform: "uppercase",
  fontWeight: 600,
};

const gradText = {
  backgroundImage: "linear-gradient(90deg,#7C3AED 0%,#EC4899 50%,#F97316 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
  fontStyle: "italic",
};

function Keyframes() {
  return (
    <style>{`
      @keyframes sceneIn {
        from { opacity: 0; transform: translateY(24px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(36px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes scrollUp {
        from { transform: translateY(0); }
        to   { transform: translateY(-50%); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.03); }
      }
    `}</style>
  );
}
